/**
 * AuthContext — central source of truth for the captain's login state.
 *
 * Auth is proxied through the Express server (/api/auth/*); the browser never
 * talks to Supabase directly. The session tokens are persisted in
 * localStorage (see lib/session.js) and read synchronously on load, so there
 * is no async bootstrap roundtrip.
 *
 * Exposes:
 *   - session: { access_token, refresh_token, expires_at, user } | null
 *   - user: shorthand for session?.user ({ id, email })
 *   - loading: kept for API compatibility (always false now — session is
 *     known synchronously from storage)
 *   - signIn(email, password): POST /api/auth/login, then persist the session
 *   - signOut(): clear the stored session
 *
 * Usage:
 *   const { user, loading, signIn, signOut } = useAuth();
 *
 * Token refresh + forced logout (on a failed refresh) are handled in
 * lib/api.js, which fires AUTH_EXPIRED_EVENT; we listen for it here and drop
 * the session so guarded routes bounce to the login page.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { loginRequest } from '../lib/api';
import {
  AUTH_EXPIRED_EVENT,
  clearSession,
  getSession,
  setSession as storeSession,
} from '../lib/session';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(() => getSession());
  // No async bootstrap anymore — kept so consumers checking `loading` work.
  const [loading] = useState(false);

  // The axios layer fires this when a token refresh fails. Drop the session
  // so RequireAuth redirects to /captain/login.
  useEffect(() => {
    const onExpired = () => setSession(null);
    window.addEventListener(AUTH_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, onExpired);
  }, []);

  const signIn = useCallback(async (email, password) => {
    const data = await loginRequest(email, password);
    storeSession(data);
    setSession(data);
    return data;
  }, []);

  const signOut = useCallback(async () => {
    clearSession();
    setSession(null);
  }, []);

  const value = useMemo(
    () => ({
      session,
      user: session?.user ?? null,
      loading,
      signIn,
      signOut,
    }),
    [session, loading, signIn, signOut],
  );

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside an <AuthProvider>');
  }
  return ctx;
}
